import React, { useState, useEffect } from "react";
import {
  addEligibilityCriteria,
  deleteEligibilityCriteria,
  updateEligibilityCriteria
} from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "./EligibilityAdminPanel.css";

function EligibilityAdminPanel() {
  const [formData, setFormData] = useState({
    jobTitle: "",
    education: "",
    skills: "",
    experience: "",
    location: "",
    minAge: "",
    maxAge: "",
    gender: "all"
  });

  const [jobTitleExtra, setJobTitleExtra] = useState("");
  const {
    threeTable,
    eligibilityCriterias,
    fetchThreeTable,
    fetchEligibilityCriterias
  } = useGlobalData();

  const [criterias, setCriterias] = useState([]);
  const [editId, setEditId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (!threeTable?.jobs?.length) {
      fetchThreeTable();
    }
    if (!eligibilityCriterias?.length) {
      fetchEligibilityCriterias();
    }
  }, [
    threeTable?.jobs?.length,
    eligibilityCriterias?.length,
    fetchThreeTable,
    fetchEligibilityCriterias
  ]);

  useEffect(() => {
    setCriterias(eligibilityCriterias || []);
    setJobs(threeTable.jobs || []);
  }, [threeTable.jobs, eligibilityCriterias]);

  useEffect(() => {
    const syncDarkMode = () => setDarkMode(localStorage.getItem("darkMode") === "true");
    window.addEventListener("storage", syncDarkMode);
    window.addEventListener("darkModeChanged", syncDarkMode);
    return () => {
      window.removeEventListener("storage", syncDarkMode);
      window.removeEventListener("darkModeChanged", syncDarkMode);
    };
  }, []);

  if (role !== "superadmin") {
    return (
      <div style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
        Access denied: Superadmin only
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      jobTitle: jobTitleExtra ? `${formData.jobTitle} ${jobTitleExtra}` : formData.jobTitle,
      skills: formData.skills || "",
      experience: formData.experience || ""
    };

    if (editId) {
      await updateEligibilityCriteria(editId, submitData);
      alert("Eligibility criteria updated!");
      setEditId(null);
    } else {
      await addEligibilityCriteria(submitData);
      alert("Eligibility criteria added!");
    }

    setFormData({
      jobTitle: "",
      education: "",
      skills: "",
      experience: "",
      location: "",
      minAge: "",
      maxAge: "",
      gender: "all"
    });
    setJobTitleExtra("");
    await fetchEligibilityCriterias();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this eligibility criteria?")) {
      await deleteEligibilityCriteria(id);
      alert("Deleted!");
      await fetchEligibilityCriterias();
    }
  };

  const handleModify = (criteria) => {
    setEditId(criteria._id);

    let mainTitle = criteria.jobTitle;
    let extra = "";
    const match = jobs.find((j) => mainTitle.startsWith(j.title));
    if (match) {
      mainTitle = match.title;
      extra = criteria.jobTitle.slice(match.title.length).trim();
    }

    setFormData({
      jobTitle: mainTitle,
      education: criteria.education,
      skills: criteria.skills,
      experience: criteria.experience,
      location: criteria.location,
      minAge: criteria.minAge,
      maxAge: criteria.maxAge,
      gender: criteria.gender || "all"
    });
    setJobTitleExtra(extra);
  };

  return (
    <div className={`eligibility-admin-panel${darkMode ? " dark" : ""}`}>
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">🎯 Eligibility Admin Panel</h2>
          <p className="resource-admin-panel__subtext">
            Yahan se job eligibility criteria manage karo. Add, view, ya delete karo.
          </p>
        </div>
        <div className="resource-admin-panel__pill">Eligibility</div>
      </div>

      <form className="eligibility-admin-panel__form" onSubmit={handleSubmit}>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Job Title</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.jobTitle}
            onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
            required
          >
            <option value="" disabled>
              Select a job
            </option>
            {jobs.map((job) => (
              <option key={job._id} value={job.title}>
                {job.title} ({job.slug})
              </option>
            ))}
          </select>
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">
            Job Title ke last me add karne wala text (optional)
          </label>
          <input
            className="eligibility-admin-panel__input"
            type="text"
            placeholder="Job Title ke last me add hoga..."
            value={jobTitleExtra}
            onChange={(e) => setJobTitleExtra(e.target.value)}
          />
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Gender</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.gender || "all"}
            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
            required
          >
            <option value="all">All</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Education Group</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.education}
            onChange={(e) => setFormData({ ...formData, education: e.target.value })}
            required
          >
            <option value="">Select Education Group</option>
            <option value="school">School/Pre-UG (10th, 12th, Diploma)</option>
            <option value="ug">UG (B.A., B.Sc., B.Com., B.Tech, BCA, BBA)</option>
            <option value="pg">PG (M.A., M.Sc., M.Com., M.Tech, MBA, MCA)</option>
            <option value="doctorate">Doctorate (PhD)</option>
          </select>
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Skills</label>
          <input
            className="eligibility-admin-panel__input"
            placeholder="Skills"
            value={formData.skills}
            onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
          />
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Experience</label>
          <input
            className="eligibility-admin-panel__input"
            placeholder="Experience"
            value={formData.experience}
            onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
          />
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Location (State/UT)</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            required
          >
            <option value="">Select State/UT</option>
            <option value="India">India</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Arunachal Pradesh">Arunachal Pradesh</option>
            <option value="Assam">Assam</option>
            <option value="Bihar">Bihar</option>
            <option value="Chhattisgarh">Chhattisgarh</option>
            <option value="Goa">Goa</option>
            <option value="Gujarat">Gujarat</option>
            <option value="Haryana">Haryana</option>
            <option value="Himachal Pradesh">Himachal Pradesh</option>
            <option value="Jharkhand">Jharkhand</option>
            <option value="Karnataka">Karnataka</option>
            <option value="Kerala">Kerala</option>
            <option value="Madhya Pradesh">Madhya Pradesh</option>
            <option value="Maharashtra">Maharashtra</option>
            <option value="Manipur">Manipur</option>
            <option value="Meghalaya">Meghalaya</option>
            <option value="Mizoram">Mizoram</option>
            <option value="Nagaland">Nagaland</option>
            <option value="Odisha">Odisha</option>
            <option value="Punjab">Punjab</option>
            <option value="Rajasthan">Rajasthan</option>
            <option value="Sikkim">Sikkim</option>
            <option value="Tamil Nadu">Tamil Nadu</option>
            <option value="Telangana">Telangana</option>
            <option value="Tripura">Tripura</option>
            <option value="Uttar Pradesh">Uttar Pradesh</option>
            <option value="Uttarakhand">Uttarakhand</option>
            <option value="West Bengal">West Bengal</option>
            <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
            <option value="Chandigarh">Chandigarh</option>
            <option value="Dadra and Nagar Haveli and Daman and Diu">
              Dadra and Nagar Haveli and Daman and Diu
            </option>
            <option value="Delhi">Delhi</option>
            <option value="Jammu and Kashmir">Jammu and Kashmir</option>
            <option value="Ladakh">Ladakh</option>
            <option value="Lakshadweep">Lakshadweep</option>
            <option value="Puducherry">Puducherry</option>
          </select>
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Min Age</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.minAge}
            onChange={(e) => setFormData({ ...formData, minAge: e.target.value })}
            required
          >
            <option value="">Select Min Age</option>
            {Array.from({ length: 45 }, (_, i) => 16 + i).map((age) => (
              <option key={age} value={age}>
                {age}
              </option>
            ))}
          </select>
        </div>

        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Max Age</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.maxAge}
            onChange={(e) => setFormData({ ...formData, maxAge: e.target.value })}
            required
          >
            <option value="">Select Max Age</option>
            {Array.from({ length: 45 }, (_, i) => 16 + i).map((age) => (
              <option key={age} value={age}>
                {age}
              </option>
            ))}
          </select>
        </div>

        <button className="eligibility-admin-panel__submit" type="submit">
          {editId ? "✅ Update Eligibility" : "✅ Add Eligibility"}
        </button>
      </form>

      <div className="resource-admin-panel__section">
        <div className="resource-admin-panel__section-top">
          <div>
            <h3 className="resource-admin-panel__section-title">Saved Eligibilities</h3>
            <p className="resource-admin-panel__section-meta">{criterias.length} items</p>
          </div>
        </div>

        {criterias.length === 0 ? (
          <p className="resource-admin-panel__empty">No eligibility criteria found</p>
        ) : (
          <div className="resource-admin-panel__list">
            {criterias.slice(0, 15).map((criteria) => {
              let statusColor = "#aaa";
              let statusText = "Unknown";
              if (criteria.status === "approved") {
                statusColor = "#3bbf3b";
                statusText = "Approved";
              } else if (criteria.status === "pending") {
                statusColor = "#ffd600";
                statusText = "Pending";
              } else if (criteria.status) {
                statusText = criteria.status.charAt(0).toUpperCase() + criteria.status.slice(1);
              }

              return (
                <div key={criteria._id} className="resource-admin-panel__item">
                  <div>
                    <p className="resource-admin-panel__item-title">{criteria.jobTitle}</p>
                    <p className="resource-admin-panel__item-subtitle">
                      Education Group: {criteria.education}, Gender:{" "}
                      {criteria.gender
                        ? criteria.gender.charAt(0).toUpperCase() + criteria.gender.slice(1)
                        : "All"}
                      , Skills: {criteria.skills}, Experience: {criteria.experience}, Location:{" "}
                      {criteria.location}, Age: {criteria.minAge}-{criteria.maxAge}
                    </p>
                  </div>
                  <div className="resource-admin-panel__actions eligibility-admin-panel__actions-responsive">
                    <span
                      className="eligibility-admin-panel__status-badge"
                      style={{
                        background: statusColor,
                        color: statusColor === "#ffd600" ? "#333" : "#fff"
                      }}
                    >
                      {statusText}
                    </span>
                    <button
                      className="resource-admin-panel__delete"
                      onClick={() => handleDelete(criteria._id)}
                    >
                      Delete
                    </button>
                    <button
                      className="resource-admin-panel__modify"
                      onClick={() => handleModify(criteria)}
                    >
                      Modify
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default EligibilityAdminPanel;